/**
 * PrimeNG Saga Purple theme preset.
 * Colors aligned with legacy saga-purple-theme.css and src/assets/layout/styles/layout/_variables.scss.
 */

import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/** Brand and semantic colors shared with layout _variables.scss */
const colors = {
  primary: '#6b24c7',
  primaryHover: '#5015b0',
  primaryActive: '#7d1f8d',
  primaryCheckedHover: '#8c239e',
  primaryContrast: '#ffffff',
  focusRing: '#df9eea',
  highlightBg: '#f3e5f5',
  highlightFocusBg: '#e1bee7',
  purple001: '#f5efff',
  text: '#495057',
  textMuted: '#6c757d',
  surfaceGround: '#f8f9fa',
  surfaceBorder: '#dee2e6',
  surfaceHover: '#e9ecef',
  formBorder: '#ced4da',
  invalid: '#f44336',
  maskBg: 'rgba(0, 0, 0, 0.4)',
  secondary: '#852196',
  secondaryHover: '#6d1b7b',
  secondaryActive: '#561561',
  novatiqBlue: '#330065'
} as const;

/** Legacy saga-purple primary / purple scale (--primary-50 … --primary-900) */
const primaryScale = {
  50: '#faf4fb',
  100: '#e7cbec',
  200: '#d4a2dd',
  300: '#c279ce',
  400: '#af50bf',
  500: colors.primary,
  600: '#852196',
  700: '#6d1b7b',
  800: '#561561',
  900: '#3e1046',
  950: '#2a0a30'
};

/** Legacy bluegray / secondary scale (Aura primitive key: `slate`, not `bluegray`) */
const slateScale = {
  50: '#f7f9f9',
  100: '#d9e0e3',
  200: '#bbc7cd',
  300: '#9caeb7',
  400: '#7e96a1',
  500: colors.secondary,
  600: '#526a76',
  700: '#435861',
  800: '#35454c',
  900: '#263238',
  950: '#1a2226'
};

/** Material-style gray surface scale from legacy theme */
const grayScale = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#eeeeee',
  300: '#e0e0e0',
  400: '#bdbdbd',
  500: '#9e9e9e',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
  950: '#121212'
};

const SagaPurplePreset = definePreset(Aura, {
  primitive: {
    purple: primaryScale,
    slate: slateScale,
    gray: grayScale,
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '3px',
      md: '3px',
      lg: '8px',
      xl: '12px'
    }
  },

  semantic: {
    transitionDuration: '0.2s',
    disabledOpacity: '0.6',
    focusRing: {
      width: '0.2rem',
      style: 'solid',
      color: colors.focusRing,
      offset: '0',
      shadow: 'none'
    },
    primary: primaryScale,
    formField: {
      borderRadius: '3px'
    },
    content: {
      borderRadius: '3px'
    },
    list: {
      option: {
        borderRadius: '3px'
      }
    },
    colorScheme: {
      light: {
        primary: {
          color: colors.primary,
          contrastColor: colors.primaryContrast,
          hoverColor: colors.primaryHover,
          activeColor: colors.primaryActive
        },

        highlight: {
          background: colors.highlightBg,
          focusBackground: colors.highlightFocusBg,
          color: colors.text,
          focusColor: colors.text
        },

        surface: {
          0: '#ffffff',
          50: grayScale[50],
          100: grayScale[100],
          200: grayScale[200],
          300: grayScale[300],
          400: grayScale[400],
          500: grayScale[500],
          600: grayScale[600],
          700: grayScale[700],
          800: grayScale[800],
          900: grayScale[900],
          950: grayScale[950]
        },

        mask: {
          background: colors.maskBg,
          color: grayScale[200]
        },

        formField: {
          background: '#ffffff',
          disabledBackground: colors.surfaceHover,
          filledBackground: colors.surfaceGround,
          filledFocusBackground: '#ffffff',
          borderColor: colors.formBorder,
          hoverBorderColor: colors.primary,
          focusBorderColor: colors.primary,
          invalidBorderColor: colors.invalid,
          color: colors.text,
          disabledColor: colors.textMuted,
          placeholderColor: colors.textMuted,
          floatLabelColor: colors.textMuted,
          floatLabelActiveColor: colors.textMuted,
          floatLabelFocusColor: colors.primary,
          floatLabelInvalidColor: colors.invalid,
          iconColor: colors.textMuted,
          shadow: 'none'
        },

        text: {
          color: colors.text,
          hoverColor: colors.text,
          mutedColor: colors.textMuted,
          hoverMutedColor: colors.textMuted
        },

        content: {
          background: '#ffffff',
          hoverBackground: colors.surfaceHover,
          borderColor: colors.surfaceBorder,
          color: colors.text,
          hoverColor: colors.text
        },

        overlay: {
          select: {
            background: '#ffffff',
            borderColor: 'transparent',
            color: colors.text
          },
          popover: {
            background: '#ffffff',
            borderColor: 'transparent',
            color: colors.text
          },
          modal: {
            background: '#ffffff',
            borderColor: 'transparent',
            color: colors.text
          }
        },

        list: {
          option: {
            focusBackground: colors.surfaceHover,
            selectedBackground: colors.highlightBg,
            selectedFocusBackground: colors.highlightFocusBg,
            color: colors.text,
            focusColor: colors.text,
            selectedColor: colors.text,
            selectedFocusColor: colors.text,
            icon: {
              color: colors.textMuted,
              focusColor: colors.textMuted
            }
          },
          optionGroup: {
            background: 'transparent',
            color: colors.text
          }
        },

        navigation: {
          item: {
            focusBackground: colors.surfaceHover,
            activeBackground: colors.highlightBg,
            color: colors.text,
            focusColor: colors.text,
            activeColor: colors.primary,
            icon: {
              color: colors.textMuted,
              focusColor: colors.textMuted,
              activeColor: colors.primary
            }
          },
          submenuLabel: {
            background: 'transparent',
            color: colors.text
          },
          submenuIcon: {
            color: colors.textMuted,
            focusColor: colors.textMuted,
            activeColor: colors.textMuted
          }
        }
      },

      dark: {
        primary: {
          color: '#ce93d8',
          hoverColor: '#ba68c8',
          activeColor: '#ab47bc'
        },
        highlight: {
          background: 'rgba(206, 147, 216, 0.16)',
          focusBackground: 'rgba(206, 147, 216, 0.24)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)'
        },
        surface: {
          0: '#121212',
          50: '#1e1e1e',
          100: '#2a2a2a',
          200: '#3a3a3a',
          300: '#4a4a4a',
          400: '#5a5a5a',
          500: '#6a6a6a',
          600: '#7a7a7a',
          700: '#8a8a8a',
          800: '#9a9a9a',
          900: '#aaaaaa',
          950: '#f5f5f5'
        }
      }
    }
  },

  components: {
    focusRing: {
      width: '0.2rem',
      style: 'solid',
      color: colors.focusRing,
      offset: '0'
    },

    button: {
      root: {
        borderRadius: '8px'
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: colors.primary,
              hoverBackground: colors.primaryHover,
              activeBackground: colors.primaryActive,
              borderColor: colors.primary,
              hoverBorderColor: colors.primaryHover,
              activeBorderColor: colors.primaryActive,
              color: colors.primaryContrast,
              hoverColor: colors.primaryContrast,
              activeColor: colors.primaryContrast
            },
            secondary: {
              background: colors.secondary,
              hoverBackground: colors.secondaryHover,
              activeBackground: colors.secondaryActive,
              borderColor: colors.secondary,
              hoverBorderColor: colors.secondaryHover,
              activeBorderColor: colors.secondaryActive,
              color: colors.primaryContrast,
              hoverColor: colors.primaryContrast,
              activeColor: colors.primaryContrast
            }
          },
          outlined: {
            primary: {
              hoverBackground: colors.purple001,
              activeBackground: 'rgba(156, 39, 176, 0.16)',
              borderColor: colors.primary,
              color: colors.primary
            },
            secondary: {
              hoverBackground: 'rgba(133, 33, 150, 0.04)',
              activeBackground: 'rgba(133, 33, 150, 0.16)',
              borderColor: colors.secondary,
              color: colors.secondary
            }
          },
          text: {
            primary: {
              hoverBackground: 'rgba(156, 39, 176, 0.04)',
              activeBackground: 'rgba(156, 39, 176, 0.16)',
              color: colors.primary
            }
          }
        }
      }
    },

    togglebutton: {
      root: {
        borderRadius: '8px'
      },
      css: `
        .p-togglebutton-checked:not(.p-disabled):hover .p-togglebutton-content {
          background: ${colors.primaryCheckedHover};
          color: ${colors.primaryContrast};
        }
      `,
      colorScheme: {
        light: {
          root: {
            background: '#ffffff',
            borderColor: colors.formBorder,
            color: colors.text,
            hoverBackground: colors.surfaceHover,
            hoverColor: colors.text,
            checkedBackground: 'transparent',
            checkedColor: colors.primaryContrast,
            checkedBorderColor: colors.formBorder
          },
          content: {
            padding: '7px 14px',
            checkedBackground: colors.primary
          }
        }
      }
    },

    checkbox: {
      colorScheme: {
        light: {
          root: {
            checkedBackground: colors.primary,
            checkedHoverBackground: colors.primaryActive,
            checkedBorderColor: colors.primary,
            checkedHoverBorderColor: colors.primaryActive
          }
        }
      }
    },

    radiobutton: {
      colorScheme: {
        light: {
          root: {
            checkedBorderColor: colors.primary,
            checkedHoverBorderColor: colors.primaryActive
          },
          icon: {
            checkedColor: colors.primary,
            checkedHoverColor: colors.primaryActive
          }
        }
      }
    },

    toggleswitch: {
      colorScheme: {
        light: {
          root: {
            checkedBackground: colors.primary,
            checkedHoverBackground: colors.primaryCheckedHover
          }
        }
      }
    },

    select: {
      colorScheme: {
        light: {
          root: {
            hoverBorderColor: colors.primary,
            focusBorderColor: colors.primary
          }
        }
      }
    },

    datepicker: {
      colorScheme: {
        light: {
          dropdown: {
            background: colors.primary,
            hoverBackground: colors.primaryHover,
            activeBackground: colors.primaryActive,
            color: colors.primaryContrast,
            hoverColor: colors.primaryContrast,
            activeColor: colors.primaryContrast
          },
          date: {
            selectedBackground: colors.highlightBg,
            selectedColor: colors.text
          }
        }
      }
    },

    datatable: {
      colorScheme: {
        light: {
          headerCell: {
            selectedBackground: colors.surfaceGround,
            selectedColor: colors.primary
          },
          row: {
            selectedBackground: colors.highlightBg,
            selectedColor: colors.text
          }
        }
      }
    },

    tabs: {
      colorScheme: {
        light: {
          tab: {
            activeBorderColor: colors.primary,
            activeColor: colors.primary
          },
          activeBar: {
            background: colors.primary
          },
          navButton: {
            color: colors.primary,
            hoverColor: colors.primary
          }
        }
      }
    },

    paginator: {
      colorScheme: {
        light: {
          navButton: {
            selectedBackground: colors.highlightBg,
            selectedColor: colors.text
          },
          currentPageReport: {
            color: colors.textMuted
          }
        }
      }
    },

    progressbar: {
      colorScheme: {
        light: {
          value: {
            background: colors.primary
          }
        }
      }
    },

    slider: {
      colorScheme: {
        light: {
          handle: {
            background: colors.primaryContrast,
            hoverBackground: colors.purple001,
            content: {
              background: colors.primary,
              hoverBackground: colors.primaryHover
            }
          },
          range: {
            background: colors.primary
          }
        }
      }
    },

    badge: {
      colorScheme: {
        light: {
          primary: {
            background: colors.primary,
            color: colors.primaryContrast
          }
        }
      }
    },

    dialog: {
      root: {
        borderRadius: '3px',
        shadow:
          '0 11px 15px -7px rgba(0, 0, 0, 0.2), 0 24px 38px 3px rgba(0, 0, 0, 0.14), 0 9px 46px 8px rgba(0, 0, 0, 0.12)'
      },
      header: {
        padding: '1.5rem'
      },
      title: {
        fontSize: '1.25rem',
        fontWeight: '600'
      },
      content: {
        padding: '0 1.5rem 2rem'
      },
      footer: {
        padding: '0 1.5rem 1.5rem'
      }
    },

    tag: {
      root: {
        fontSize: '0.75rem',
        fontWeight: '400',
        padding: '0.25rem 0.4rem',
        borderRadius: '3px'
      },
      colorScheme: {
        light: {
          primary: {
            background: colors.primary,
            color: colors.primaryContrast
          },
          success: {
            background: '#689f38',
            color: colors.primaryContrast
          },
          info: {
            background: '#0288d1',
            color: colors.primaryContrast
          },
          warn: {
            background: '#fbc02d',
            color: '#212529'
          },
          danger: {
            background: '#d32f2f',
            color: colors.primaryContrast
          },
          secondary: {
            background: colors.surfaceHover,
            color: colors.text
          }
        }
      }
    },

    chip: {
      css: `
        .p-datatable-header .p-chip,
        .p-datatable-header .p-chip-label,
        .p-treetable-header .p-chip,
        .p-treetable-header .p-chip-label {
          font-weight: 400;
        }
      `,
      colorScheme: {
        light: {
          root: {
            background: colors.surfaceHover,
            color: colors.text
          }
        }
      }
    },

    message: {
      colorScheme: {
        light: {
          info: {
            background: '#b3e5fc',
            borderColor: '#0891cf',
            color: '#044868'
          },
          success: {
            background: '#c8e6c9',
            borderColor: '#439446',
            color: '#224a23'
          },
          warn: {
            background: '#ffecb3',
            borderColor: '#d9a300',
            color: '#6d5100'
          },
          error: {
            background: '#ffcdd2',
            borderColor: '#e60017',
            color: '#73000c'
          }
        }
      }
    },

    fieldset: {
      root: {
        background: '{content.background}',
        borderColor: '{content.border.color}',
        borderRadius: '{content.border.radius}',
        color: '{content.color}',
        padding: '0 1rem 1rem'
      },
      legend: {
        background: '{content.background}',
        hoverBackground: '{content.background}',
        color: '{content.color}',
        hoverColor: '{content.color}',
        borderWidth: '0',
        borderColor: 'transparent',
        borderRadius: '0',
        padding: '0 0.5rem',
        fontWeight: '600'
      },
      content: {
        padding: '0.75rem 0 0'
      }
    }
  },

  css: `
    :root {
      --primary-purple: ${colors.primary};
      --secondary-purple: ${colors.primaryHover};
      --secondary-color: ${colors.primaryHover};
      --purple-001: ${colors.purple001};
      --purple-005: #4c227e;
      --purple-006: #391861;
      --novatiq-blue: ${colors.novatiqBlue};
      --surface-ground: ${colors.surfaceGround};
      --surface-border: ${colors.surfaceBorder};
      --surface-hover: ${colors.surfaceHover};
      --text-color: ${colors.text};
      --text-color-secondary: ${colors.textMuted};
      --focus-ring: 0 0 0 0.2rem ${colors.focusRing};
      --border-radius: 3px;
      --button-border: 8px;
      --creator-row-selected-bg: ${colors.purple001};
      --creator-row-selected-accent: ${primaryScale[200]};
    }
  `
});

export default SagaPurplePreset;
